import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import {
  AddBusinessOutlined as AddCustomerIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  WarningAmberRounded as WarningIcon,
} from '@mui/icons-material';
import PageHeader, { PageContainer } from '../components/PageHeader';
import { SearchFilter, TogglePill } from '../components/monthly/MultiSelectFilter';
import { useFillViewportHeight } from '../components/monthly/tableStyles';
import CustomerPanel, { CustomerBadge } from '../components/customers/CustomerPanel';
import { GREEN, GREEN_SOFT, INK, LINE, MUTED } from '../components/home/homeTheme';
import { fetchCustomersBundle } from '../utils/customersService';

const PAGE_SIZE = 100;
const EMPTY = [];
const num = (n) => Number(n || 0).toLocaleString('en-GB');
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const headSx = { fontSize: 12, fontWeight: 700, color: MUTED, bgcolor: 'var(--rt-paper)', py: 1, borderBottom: `1px solid ${LINE}`, whiteSpace: 'nowrap' };
const cellSx = { fontSize: 13, color: INK, py: 0.9, borderBottom: `1px solid ${LINE}` };

/**
 * Customers V2: elenco clienti con progetti e contratti collegati e pannello laterale
 * (dettaglio, modifica, creazione, eliminazione). Il cliente aperto è nell'URL (?customer=ID).
 */
export default function CustomersV2() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get('customer');
  const creating = searchParams.get('new') === '1';

  const [refreshToken, setRefreshToken] = useState(0);
  const [data, setData] = useState({ customers: EMPTY, projects: EMPTY, contracts: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchCustomersBundle()
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(`Unable to load customers: ${e.message}`))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  const { customers, projects, contracts } = data;

  // ---- Progetti e contratti per cliente ----
  const byCustomer = useMemo(() => {
    const map = {};
    const entry = (id) => {
      const key = String(id);
      if (!map[key]) map[key] = { projects: [], contracts: [] };
      return map[key];
    };
    projects.forEach((p) => {
      const id = p.customer_id ?? p.customer?.id;
      if (id != null) entry(id).projects.push(p);
    });
    (contracts || []).forEach((c) => {
      if (c.customer_id != null) entry(c.customer_id).contracts.push(c);
    });
    return map;
  }, [projects, contracts]);
  const linkedOf = (id) => byCustomer[String(id)] || { projects: EMPTY, contracts: EMPTY };
  const activeCount = (id) => linkedOf(id).projects.filter((p) => p.active).length;
  const withActive = useMemo(() => customers.filter((c) => (byCustomer[String(c.id)]?.projects || []).some((p) => p.active)).length, [customers, byCustomer]);

  // ---- Filtri e ordinamento ----
  const [search, setSearch] = useState('');
  const [onlyInactive, setOnlyInactive] = useState(false);
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [page, setPage] = useState(0);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = customers.filter((c) => {
      if (onlyInactive && (byCustomer[String(c.id)]?.projects || []).some((p) => p.active)) return false;
      if (!q) return true;
      return [c.name, c.description, String(c.id)].some((v) => String(v || '').toLowerCase().includes(q));
    });
    const projectsOf = (c) => byCustomer[String(c.id)]?.projects || EMPTY;
    const sorters = {
      name: (a, b) => String(a.name || '').localeCompare(String(b.name || '')),
      projects: (a, b) =>
        projectsOf(a).filter((p) => p.active).length - projectsOf(b).filter((p) => p.active).length || projectsOf(a).length - projectsOf(b).length,
      contracts: (a, b) => (byCustomer[String(a.id)]?.contracts.length || 0) - (byCustomer[String(b.id)]?.contracts.length || 0),
      updated: (a, b) => String(a.updated_at || '').localeCompare(String(b.updated_at || '')),
    };
    list.sort((a, b) => sorters[sort.key](a, b) || sorters.name(a, b));
    if (sort.dir === 'desc') list.reverse();
    return list;
  }, [customers, search, onlyInactive, sort, byCustomer]);

  useEffect(() => setPage(0), [rows.length, sort]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ---- Pannello ----
  const openCustomer = customers.find((c) => String(c.id) === String(openId)) || null;
  const showCustomer = (id) => setSearchParams({ customer: String(id) });
  const closePanel = () => setSearchParams({});
  const afterChange = (message, id) => {
    setSnack(message);
    setRefreshToken((t) => t + 1);
    if (id) showCustomer(id);
    else closePanel();
  };

  const tableRef = useRef(null);
  const fillHeight = useFillViewportHeight(tableRef, [loading, error]);

  const sortHeader = (key, label, align) => (
    <TableCell sx={{ ...headSx, textAlign: align }} sortDirection={sort.key === key ? sort.dir : false}>
      <TableSortLabel
        active={sort.key === key}
        direction={sort.key === key ? sort.dir : 'asc'}
        onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const openLinked = openCustomer ? linkedOf(openCustomer.id) : { projects: EMPTY, contracts: EMPTY };

  return (
    <PageContainer>
      <PageHeader title="Customers" subtitle={loading ? 'Loading…' : `${num(customers.length)} customers · ${num(withActive)} with active projects`}>
        <Button
          variant="contained"
          color="success"
          startIcon={<AddCustomerIcon />}
          onClick={() => setSearchParams({ new: '1' })}
          disabled={loading}
          sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', height: 40, bgcolor: GREEN }}
        >
          Add customer
        </Button>
      </PageHeader>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Filtri */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <SearchFilter value={search} onChange={setSearch} placeholder="Search name or description" width={300} />
          <TogglePill
            label={`No active projects (${num(customers.length - withActive)})`}
            checked={onlyInactive}
            onChange={setOnlyInactive}
            icon={<WarningIcon />}
          />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box ref={tableRef} sx={{ border: `1px solid ${LINE}`, borderRadius: 3, bgcolor: 'var(--rt-paper)', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress color="success" />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: fillHeight ? fillHeight - 48 : 'calc(100vh - 260px)' }}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 720 }}>
                  <colgroup>
                    <col />
                    <col style={{ width: 150 }} />
                    {contracts && <col style={{ width: 120 }} />}
                    <col style={{ width: 150 }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      {sortHeader('name', 'Customer')}
                      {sortHeader('projects', 'Active projects', 'right')}
                      {/* Se l'elenco dei contratti non è disponibile la colonna non compare */}
                      {contracts && sortHeader('contracts', 'Contracts', 'right')}
                      {sortHeader('updated', 'Last updated', 'right')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.map((c) => {
                      const selected = String(c.id) === String(openId);
                      const linked = linkedOf(c.id);
                      const active = activeCount(c.id);
                      return (
                        <TableRow
                          key={c.id}
                          hover
                          selected={selected}
                          onClick={() => showCustomer(c.id)}
                          sx={{ cursor: 'pointer', '&.Mui-selected, &.Mui-selected:hover': { bgcolor: GREEN_SOFT } }}
                        >
                          <TableCell sx={cellSx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                              <CustomerBadge size={32} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: INK }}>
                                  {c.name}
                                </Typography>
                                <Typography noWrap sx={{ fontSize: 12, color: MUTED }} title={c.description || ''}>
                                  {c.description || '—'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...cellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            <Box component="span" sx={{ fontWeight: 700, color: active ? INK : MUTED }}>
                              {active}
                            </Box>
                            <Box component="span" sx={{ color: MUTED }}>
                              {' '}
                              / {linked.projects.length}
                            </Box>
                          </TableCell>
                          {contracts && (
                            <TableCell sx={{ ...cellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: linked.contracts.length ? INK : MUTED }}>
                              {linked.contracts.length || '—'}
                            </TableCell>
                          )}
                          <TableCell sx={{ ...cellSx, textAlign: 'right', color: MUTED, whiteSpace: 'nowrap' }}>{fmtDate(c.updated_at)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {!pageRows.length && (
                      <TableRow>
                        <TableCell colSpan={contracts ? 4 : 3} sx={{ ...cellSx, py: 4, textAlign: 'center', color: MUTED }}>
                          No customers match these filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderTop: `1px solid ${LINE}`, bgcolor: 'var(--rt-subtle-2)' }}>
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  {num(rows.length)} customer{rows.length === 1 ? '' : 's'}
                  {rows.length !== customers.length ? ` of ${num(customers.length)}` : ''} · click a row to open it
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 13, color: MUTED }}>
                    {rows.length ? `${num(page * PAGE_SIZE + 1)}–${num(Math.min(rows.length, (page + 1) * PAGE_SIZE))} of ${num(rows.length)}` : '0 of 0'}
                  </Typography>
                  <IconButton size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)} aria-label="Previous page">
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)} aria-label="Next page">
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <CustomerPanel
        open={!!openCustomer || creating}
        customer={creating ? null : openCustomer}
        creating={creating}
        customers={customers}
        projects={openLinked.projects}
        contracts={contracts ? openLinked.contracts : null}
        onClose={closePanel}
        onSaved={(id, message) => afterChange(message, id)}
        onDeleted={(message) => afterChange(message, null)}
        onOpenProject={(id) => navigate(`/projects/${id}`)}
      />

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </PageContainer>
  );
}
